import { useMemo, useState } from 'react'
import { useCrosswordStore } from '../store'
import { getLetters, getPairingByWords, getWordsInGroup, passesUsedFilter } from '../lib/pairings'
import { PairDetailColumn } from './PairDetailColumn'
import { StatusFilter } from './StatusFilter'
import { TagFilterRow, passesTagFilter, DEFAULT_TAG_FILTER_STATE, type TagFilterState } from './TagFilterRow'
import { RatingFilter, passesRatingFilter, DEFAULT_RATING_FILTER_STATE, type RatingFilterState } from './RatingFilter'
import type { Pairing, PairingStatus, ClueStatus, Word } from '../types'

/**
 * Top-level Pairs page. Three-column workflow:
 *   1. Words by length (A and B side-by-side per length section)
 *   2. Valid pair candidates for the hovered/selected word + group variants
 *   3. Pair detail (status, rating, tags, clue editor) for the picked pairing
 *
 * Hover previews column 2 / 3; click commits as the selected word or pair.
 * Clicking an unrecorded pair lazy-creates a Pairing record via
 * getOrCreatePairing and selects it. The same data model used in Construct.
 */
export function PairExplorerView() {
  const { words, pairings } = useCrosswordStore()

  // Word selection — click commits, hover previews. Active word for column 2
  // is `hoveredWordId ?? selectedWordId`. Reaching for column 2 doesn't reset
  // the panel because the click locked the selection in.
  const [selectedWordId, setSelectedWordId] = useState<string | null>(null)
  const [hoveredWordId, setHoveredWordId] = useState<string | null>(null)
  // Pair selection by word IDs — record-agnostic. Picking a pair doesn't
  // materialize a Pairing record; the record is created lazily on first
  // metadata change inside PairDetailColumn (see ensureRecord). Keeps the
  // pairings table from filling up with empty drafts as the user browses.
  const [selectedPair, setSelectedPair] = useState<{ aId: string; bId: string } | null>(null)

  const activeWordId = hoveredWordId ?? selectedWordId
  const activeWord = activeWordId ? words.find(w => w.id === activeWordId) ?? null : null

  // Group expansion: pair candidates for every variant in the active word's
  // group, not just the directly-active one (e.g. APPLE selected → also show
  // APPLES pairings).
  const groupSourceWords: Word[] = activeWord ? getWordsInGroup(activeWord, words) : []
  const groupHighlightIds = new Set(groupSourceWords.map(w => w.id))

  // Lifted filter state for the candidate-pairings column.
  const [statusFilters, setStatusFilters] = useState<PairingStatus[]>([])
  const [clueFilters, setClueFilters] = useState<(ClueStatus | 'empty')[]>([])
  const [usedFilter, setUsedFilter] = useState<'all' | 'used' | 'unused'>('all')
  const [ratingFilter, setRatingFilter] = useState<RatingFilterState>(DEFAULT_RATING_FILTER_STATE)
  const [tagFilters, setTagFilters] = useState<TagFilterState>(DEFAULT_TAG_FILTER_STATE)

  // "Used" scope: a pairing is used if any draft places it. We treat all
  // drafts (not just the active one) as the scope here because the Pairs
  // page is a global view — not tied to a particular draft.
  const drafts = useCrosswordStore(s => s.drafts)
  const placedPairingIds = useMemo(() => {
    const ids = new Set<string>()
    for (const d of drafts) for (const p of d.grid.placements) {
      if (p.pairingId) ids.add(p.pairingId)
    }
    return ids
  }, [drafts])

  // Tags placed in any draft — feeds the "no theme repeats" chip.
  const usedTagsAcrossDrafts = useMemo(() => {
    const tags = new Set<string>()
    const pairingsById = new Map(pairings.map(p => [p.id, p]))
    for (const d of drafts) for (const placement of d.grid.placements) {
      if (!placement.pairingId) continue
      const p = pairingsById.get(placement.pairingId)
      if (!p?.tags) continue
      for (const t of p.tags) tags.add(t)
    }
    return tags
  }, [drafts, pairings])

  // Sections grouped by length; A and B side-by-side per section.
  const lengthSections = useMemo(() => {
    const buckets = new Map<number, { a: Word[]; b: Word[] }>()
    for (const w of words) {
      if (!w.list) continue
      const len = getLetters(w).length
      const bucket = buckets.get(len) ?? { a: [], b: [] }
      if (w.list === 'A') bucket.a.push(w)
      else bucket.b.push(w)
      buckets.set(len, bucket)
    }
    return Array.from(buckets.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([length, ws]) => ({
        length,
        a: ws.a.sort((a, b) => a.text.localeCompare(b.text)),
        b: ws.b.sort((a, b) => a.text.localeCompare(b.text)),
      }))
  }, [words])

  // The Pairing record for the selected pair, if one exists. Re-derives when
  // the user takes an action that materializes a record (column 3's
  // ensureRecord), so the panel transitions from "ephemeral" to "recorded"
  // without a re-click.
  const selectedPairing = useMemo(() => {
    if (!selectedPair) return null
    return getPairingByWords(pairings, selectedPair.aId, selectedPair.bId)
  }, [selectedPair, pairings])

  const handleSelectWord = (id: string) => {
    setSelectedWordId(id)
    setHoveredWordId(null)
    setSelectedPair(null)
  }

  // Click on a pair candidate row → just remember the selection. The record
  // (if any) is looked up; if none, column 3 enters ephemeral mode and
  // creates the record only when the user touches metadata.
  const handlePickPair = (aId: string, bId: string) => {
    setSelectedPair({ aId, bId })
  }

  return (
    <div className="h-full flex flex-col bg-gray-100">
      {/* Header */}
      <div className="px-6 py-3 flex items-center justify-between gap-4 shrink-0">
        <h1 className="text-lg font-semibold text-gray-900">Pairs</h1>
      </div>

      {/* Filter context strip — applies to candidate pairings in column 2. */}
      <div className="px-6 pb-2 shrink-0 space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <StatusFilter
            selectedPairingStatuses={statusFilters}
            selectedClueStatuses={clueFilters}
            onPairingStatusChange={setStatusFilters}
            onClueStatusChange={setClueFilters}
            compact
          />
          <span className="text-gray-200">|</span>
          <UsedFilterChips value={usedFilter} onChange={setUsedFilter} />
          <span className="text-gray-200">|</span>
          <RatingFilter state={ratingFilter} onChange={setRatingFilter} />
        </div>
        <TagFilterRow state={tagFilters} onChange={setTagFilters} />
      </div>

      {/* Three-column body */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="h-full grid grid-cols-[1.4fr_1fr_360px] gap-3 px-6 pb-6 min-h-0">
          {/* Column 1 — words by length */}
          <div className="bg-white rounded-lg shadow-sm flex flex-col overflow-hidden min-h-0">
            <div className="px-4 py-2 border-b border-gray-100 shrink-0 text-[11px] font-medium uppercase tracking-wider text-gray-500">
              Words · by length
            </div>
            <div className="flex-1 overflow-y-auto">
              {lengthSections.length === 0 && (
                <p className="px-4 py-6 text-xs text-gray-400">No words yet.</p>
              )}
              {lengthSections.map(section => (
                <LengthSection
                  key={section.length}
                  length={section.length}
                  listA={section.a}
                  listB={section.b}
                  selectedId={selectedWordId}
                  hoveredId={hoveredWordId}
                  groupHighlightIds={groupHighlightIds}
                  onSelect={handleSelectWord}
                  onHover={setHoveredWordId}
                />
              ))}
            </div>
          </div>

          {/* Column 2 — pair candidates for the active word + group variants */}
          <div className="bg-white rounded-lg shadow-sm flex flex-col overflow-hidden min-h-0">
            <div className="px-4 py-2 border-b border-gray-100 shrink-0">
              <div className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
                Pairings
              </div>
              {activeWord ? (
                <div className="mt-1 text-sm">
                  <span className="font-mono text-gray-900">{activeWord.text}</span>
                  {groupSourceWords.length > 1 && (
                    <span className="ml-2 text-[11px] text-gray-400">
                      group: {groupSourceWords.map(w => w.text).join(', ')}
                    </span>
                  )}
                </div>
              ) : (
                <div className="mt-1 text-xs text-gray-400">Hover or click a word.</div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {activeWord ? (
                <PairingsForGroup
                  groupSourceWords={groupSourceWords}
                  allWords={words}
                  pairings={pairings}
                  selectedPair={selectedPair}
                  onPickPair={handlePickPair}
                  onSelectWord={handleSelectWord}
                  onHoverWord={setHoveredWordId}
                  statusFilters={statusFilters}
                  clueFilters={clueFilters}
                  usedFilter={usedFilter}
                  placedPairingIds={placedPairingIds}
                  ratingFilter={ratingFilter}
                  tagFilters={tagFilters}
                  usedTagsInGrid={usedTagsAcrossDrafts}
                />
              ) : (
                <div className="px-4 py-6 text-xs text-gray-400">
                  Hover a word on the left to preview its valid pairings.
                </div>
              )}
            </div>
          </div>

          {/* Column 3 — full pair detail (same component as Construct's
              assistant). Grid is null since we're not anchored to a grid
              here; placements / solutions sections degrade gracefully. */}
          <div className="min-h-0">
            {selectedPair ? (
              <PairDetailColumn
                pairing={selectedPairing}
                ephemeralPair={selectedPairing
                  ? undefined
                  : { wordAId: selectedPair.aId, wordBId: selectedPair.bId }}
                grid={null}
                onClose={() => setSelectedPair(null)}
                onShowCrossings={() => { /* no-op — no grid context here */ }}
                crossingsOpen={false}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-sm h-full flex items-center justify-center text-sm text-gray-400 px-6">
                Pick a pairing in the middle to edit it.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ──────────────────────────────────────────────────────────────────────────
 * Column 1 helpers
 * ────────────────────────────────────────────────────────────────────────── */

function LengthSection({
  length,
  listA,
  listB,
  selectedId,
  hoveredId,
  groupHighlightIds,
  onSelect,
  onHover,
}: {
  length: number
  listA: Word[]
  listB: Word[]
  selectedId: string | null
  hoveredId: string | null
  groupHighlightIds: Set<string>
  onSelect: (id: string) => void
  onHover: (id: string | null) => void
}) {
  const totalA = listA.length
  const totalB = listB.length
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div className="px-4 py-1.5 sticky top-0 bg-white/95 backdrop-blur-sm z-10 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
          {length} letters
        </span>
        <span className="text-[10px] tabular-nums text-gray-400">
          {totalA} · {totalB}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-px bg-gray-100">
        <div className="bg-white">
          {listA.map(word => (
            <WordButton
              key={word.id}
              word={word}
              selected={selectedId === word.id}
              hovered={hoveredId === word.id}
              groupHighlight={
                selectedId !== word.id &&
                hoveredId !== word.id &&
                groupHighlightIds.has(word.id)
              }
              onSelect={() => onSelect(word.id)}
              onHoverEnter={() => onHover(word.id)}
              onHoverLeave={() => onHover(null)}
            />
          ))}
          {listA.length === 0 && (
            <p className="px-3 py-2 text-[11px] text-gray-300">—</p>
          )}
        </div>
        <div className="bg-white">
          {listB.map(word => (
            <WordButton
              key={word.id}
              word={word}
              selected={selectedId === word.id}
              hovered={hoveredId === word.id}
              groupHighlight={
                selectedId !== word.id &&
                hoveredId !== word.id &&
                groupHighlightIds.has(word.id)
              }
              onSelect={() => onSelect(word.id)}
              onHoverEnter={() => onHover(word.id)}
              onHoverLeave={() => onHover(null)}
            />
          ))}
          {listB.length === 0 && (
            <p className="px-3 py-2 text-[11px] text-gray-300">—</p>
          )}
        </div>
      </div>
    </div>
  )
}

function WordButton({
  word,
  selected,
  hovered,
  groupHighlight,
  onSelect,
  onHoverEnter,
  onHoverLeave,
}: {
  word: Word
  selected: boolean
  hovered: boolean
  groupHighlight?: boolean
  onSelect: () => void
  onHoverEnter: () => void
  onHoverLeave: () => void
}) {
  // Visual hierarchy:
  //   selected — strong (committed pick)
  //   hovered — strong (transient pick taking precedence)
  //   group   — soft tint (variant of the selected word)
  //   default — plain
  const tone = selected
    ? 'bg-blue-100 text-blue-900'
    : hovered
      ? 'bg-blue-50 text-blue-900'
      : groupHighlight
        ? 'bg-blue-50/60 text-blue-800'
        : 'text-gray-700 hover:bg-gray-50'
  return (
    <button
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
      onClick={onSelect}
      className={`w-full text-left px-3 py-1 text-sm font-mono transition-colors ${tone}`}
    >
      {word.text}
    </button>
  )
}

/* ──────────────────────────────────────────────────────────────────────────
 * Column 2 — pairings for active word, expanded across group variants.
 * ────────────────────────────────────────────────────────────────────────── */

type PairCandidateFilters = {
  statusFilters: ReadonlyArray<PairingStatus>
  clueFilters: ReadonlyArray<ClueStatus | 'empty'>
  usedFilter: 'all' | 'used' | 'unused'
  placedPairingIds: ReadonlySet<string>
  ratingFilter: RatingFilterState
  tagFilters: TagFilterState
  usedTagsInGrid: ReadonlySet<string>
}

function PairingsForGroup({
  groupSourceWords,
  allWords,
  pairings,
  selectedPair,
  onPickPair,
  onSelectWord,
  onHoverWord,
  ...filters
}: PairCandidateFilters & {
  groupSourceWords: Word[]
  allWords: Word[]
  pairings: Pairing[]
  selectedPair: { aId: string; bId: string } | null
  onPickPair: (aId: string, bId: string) => void
  onSelectWord: (wordId: string) => void
  onHoverWord: (wordId: string | null) => void
}) {
  return (
    <div className="divide-y divide-gray-100">
      {groupSourceWords.map(source => (
        <PairingsForSource
          key={source.id}
          source={source}
          allWords={allWords}
          pairings={pairings}
          selectedPair={selectedPair}
          onPickPair={onPickPair}
          onSelectWord={onSelectWord}
          onHoverWord={onHoverWord}
          {...filters}
        />
      ))}
    </div>
  )
}

function PairingsForSource({
  source,
  allWords,
  pairings,
  selectedPair,
  onPickPair,
  onSelectWord,
  onHoverWord,
  statusFilters,
  clueFilters,
  usedFilter,
  placedPairingIds,
  ratingFilter,
  tagFilters,
  usedTagsInGrid,
}: PairCandidateFilters & {
  source: Word
  allWords: Word[]
  pairings: Pairing[]
  selectedPair: { aId: string; bId: string } | null
  onPickPair: (aId: string, bId: string) => void
  onSelectWord: (wordId: string) => void
  onHoverWord: (wordId: string | null) => void
}) {
  const len = getLetters(source).length
  const otherListSide = source.list === 'A' ? 'B' : source.list === 'B' ? 'A' : null
  if (!otherListSide) return null

  const candidates = allWords.filter(
    w => w.list === otherListSide && getLetters(w).length === len,
  )

  // Enrich each candidate with any existing pairing record + run through
  // the active filter chain.
  const enriched = candidates
    .map(other => {
      const aId = source.list === 'A' ? source.id : other.id
      const bId = source.list === 'A' ? other.id : source.id
      const recorded = getPairingByWords(pairings, aId, bId)
      return { aId, bId, other, recorded }
    })
    .filter(item => {
      const status = item.recorded?.status ?? 'none'
      // Status: if no chips selected, hide rejected by default.
      if (statusFilters.length === 0) {
        if (status === 'rejected') return false
      } else {
        if (!statusFilters.includes(status)) return false
      }
      // Clue: filter by recorded clue state (or 'empty' for unrecorded).
      if (clueFilters.length > 0) {
        const clueState: ClueStatus | 'empty' = item.recorded?.clue?.text ? item.recorded.clue.status : 'empty'
        if (!clueFilters.includes(clueState)) return false
      }
      // Used: unrecorded pairs are unused. passesUsedFilter handles both.
      if (usedFilter !== 'all') {
        if (!passesUsedFilter(item.recorded?.id ?? null, usedFilter, placedPairingIds)) return false
      }
      // Rating: unrecorded pairs have no rating.
      if (ratingFilter.threshold !== null) {
        if (!passesRatingFilter(item.recorded?.rating, ratingFilter)) return false
      }
      // Tags: unrecorded pairs have no tags.
      if (tagFilters.include.length > 0 || tagFilters.exclude.length > 0 || tagFilters.excludeUsedThemes) {
        if (!passesTagFilter(item.recorded?.tags, tagFilters, usedTagsInGrid)) return false
      }
      return true
    })

  // Sort: recorded pairings first by status priority, then alphabetically.
  enriched.sort((a, b) => {
    const aRank = statusRank(a.recorded?.status)
    const bRank = statusRank(b.recorded?.status)
    if (aRank !== bRank) return aRank - bRank
    return a.other.text.localeCompare(b.other.text)
  })

  return (
    <div className="py-1">
      <div className="px-3 py-1.5 flex items-center justify-between">
        <span className="text-[11px] font-semibold text-gray-700">
          {source.text}
          <span className="ml-2 text-[10px] text-gray-400">
            list {source.list} · {len} letters
          </span>
        </span>
        <span className="text-[10px] tabular-nums text-gray-400">
          {enriched.length} options
        </span>
      </div>
      <div className="px-2">
        {enriched.length === 0 && (
          <p className="px-3 py-1.5 text-[11px] text-gray-400">No matches.</p>
        )}
        {enriched.map(item => {
          const isPicked = !!selectedPair && selectedPair.aId === item.aId && selectedPair.bId === item.bId
          return (
            <div
              key={`${item.aId}:${item.bId}`}
              role="button"
              tabIndex={0}
              onClick={() => onPickPair(item.aId, item.bId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onPickPair(item.aId, item.bId)
                }
              }}
              className={`group w-full flex items-center justify-between gap-2 px-2 py-1 rounded text-sm cursor-pointer transition-colors ${
                isPicked ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-50'
              }`}
            >
              <span className="font-mono">
                {source.list === 'A'
                  ? <>
                      <span className="text-gray-900">{source.text}</span>
                      <span className="text-gray-400 mx-1">×</span>
                      <CandidateWord
                        text={item.other.text}
                        wordId={item.other.id}
                        onSelect={onSelectWord}
                        onHover={onHoverWord}
                      />
                    </>
                  : <>
                      <CandidateWord
                        text={item.other.text}
                        wordId={item.other.id}
                        onSelect={onSelectWord}
                        onHover={onHoverWord}
                      />
                      <span className="text-gray-400 mx-1">×</span>
                      <span className="text-gray-900">{source.text}</span>
                    </>}
              </span>
              <span className="flex items-center gap-2 shrink-0">
                {item.recorded?.clue?.text && (
                  <span className="text-[10px] text-emerald-600" title={item.recorded.clue.text}>
                    clue
                  </span>
                )}
                <StatusDot status={item.recorded?.status} />
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * The candidate word in a pairing row — hover previews that word in column 2,
 * click commits it as the new selection. Click stops propagation so it
 * doesn't also fire the row-level "pick this pair" handler. Hover underline
 * is the only affordance — no extra button chrome.
 */
function CandidateWord({
  text,
  wordId,
  onSelect,
  onHover,
}: {
  text: string
  wordId: string
  onSelect: (wordId: string) => void
  onHover: (wordId: string | null) => void
}) {
  return (
    <span
      role="button"
      tabIndex={0}
      onMouseEnter={() => onHover(wordId)}
      onMouseLeave={() => onHover(null)}
      onClick={(e) => {
        e.stopPropagation()
        onSelect(wordId)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          e.stopPropagation()
          onSelect(wordId)
        }
      }}
      className="text-gray-700 hover:underline hover:text-blue-700 underline-offset-2 cursor-pointer"
    >
      {text}
    </span>
  )
}

function StatusDot({ status }: { status: PairingStatus | undefined }) {
  if (!status || status === 'none') {
    return <span className="w-2 h-2 rounded-full border border-gray-300" title="unrecorded" />
  }
  const colorClass = {
    approved: 'bg-emerald-400',
    draft: 'bg-sky-400',
    rejected: 'bg-gray-300',
  }[status]
  return <span className={`w-2 h-2 rounded-full ${colorClass}`} title={status} />
}

function statusRank(status: PairingStatus | undefined): number {
  switch (status) {
    case 'approved': return 0
    case 'draft': return 1
    case 'rejected': return 3
    default: return 2 // unrecorded / none
  }
}

/* ──────────────────────────────────────────────────────────────────────────
 * Used / unused filter chips — match the icon-button shape of StatusFilter
 * for visual consistency.
 * ────────────────────────────────────────────────────────────────────────── */

function UsedFilterChips({
  value,
  onChange,
}: {
  value: 'all' | 'used' | 'unused'
  onChange: (next: 'all' | 'used' | 'unused') => void
}) {
  const buttons = [
    { state: 'used' as const, icon: '●', tone: 'bg-blue-100 text-blue-600', title: 'Show only pairs placed in some draft' },
    { state: 'unused' as const, icon: '○', tone: 'bg-violet-100 text-violet-600', title: 'Show only pairs not placed anywhere' },
  ]
  return (
    <div className="flex gap-1">
      {buttons.map(({ state, icon, tone, title }) => {
        const isActive = value === state
        return (
          <button
            key={state}
            onClick={() => onChange(isActive ? 'all' : state)}
            title={title}
            className={`w-7 h-7 text-xs rounded-md transition-all flex items-center justify-center ${
              isActive ? tone : 'bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            }`}
          >
            {icon}
          </button>
        )
      })}
    </div>
  )
}
